/**
 * 通知 Store
 * 管理站内通知状态
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { 
  getNotifications, 
  getUnreadCount, 
  markAsRead, 
  markAllAsRead,
  deleteNotification,
  type NotificationItem 
} from '@/api/notifications';

export const useNotificationsStore = defineStore('notifications', () => {
  // 通知列表
  const notifications = ref<NotificationItem[]>([]);
  // 未读数量
  const unreadCount = ref(0);
  // 加载状态
  const loading = ref(false);
  // 总数
  const total = ref(0);
  // 当前页
  const currentPage = ref(1);
  // 每页数量
  const pageSize = ref(20);

  // 是否有未读通知
  const hasUnread = computed(() => unreadCount.value > 0);

  /**
   * 获取未读数量
   */
  async function fetchUnreadCount() {
    try {
      const data = await getUnreadCount();
      unreadCount.value = data.count;
    } catch (error) {
      console.error('获取未读数量失败:', error);
    }
  }

  /**
   * 获取通知列表
   */
  async function fetchNotifications(page = 1) {
    if (loading.value) return;
    
    loading.value = true;
    try {
      const data = await getNotifications({ page, pageSize: pageSize.value });
      notifications.value = data.list;
      total.value = data.total;
      currentPage.value = page;
    } catch (error) {
      console.error('获取通知列表失败:', error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * 标记单条为已读
   */
  async function markRead(id: string) {
    try {
      await markAsRead(id);
      // 更新本地状态
      const notification = notifications.value.find(n => n.id === id);
      if (notification && !notification.isRead) {
        notification.isRead = true;
        unreadCount.value = Math.max(0, unreadCount.value - 1);
      }
    } catch (error) {
      console.error('标记已读失败:', error);
      throw error;
    }
  }

  /**
   * 标记全部为已读
   */
  async function markAllRead() {
    try {
      await markAllAsRead();
      // 更新本地状态
      notifications.value.forEach(n => {
        n.isRead = true;
      });
      unreadCount.value = 0;
    } catch (error) {
      console.error('全部标记已读失败:', error);
      throw error;
    }
  }

  /**
   * 删除通知
   */
  async function removeNotification(id: string) {
    try {
      await deleteNotification(id);
      // 更新本地状态
      const index = notifications.value.findIndex(n => n.id === id);
      if (index !== -1) {
        const notification = notifications.value[index];
        if (!notification.isRead) {
          unreadCount.value = Math.max(0, unreadCount.value - 1);
        }
        notifications.value.splice(index, 1);
        total.value = Math.max(0, total.value - 1);
      }
    } catch (error) {
      console.error('删除通知失败:', error);
      throw error;
    }
  }

  /**
   * 重置状态（登出时调用）
   */
  function reset() {
    notifications.value = [];
    unreadCount.value = 0;
    total.value = 0;
    currentPage.value = 1;
  }

  return {
    notifications,
    unreadCount,
    loading,
    total,
    currentPage,
    pageSize,
    hasUnread,
    fetchUnreadCount,
    fetchNotifications,
    markRead,
    markAllRead,
    removeNotification,
    reset,
  };
});
